import { useState } from 'react';
import type {
  AuditPruneApprovalItem,
  AuditPruneApprovalStatusFilter,
  AuditPruneDecision,
} from '../../api/adminOps';
import { useAuth } from '../../components/AuthProvider';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../../components/ui';
import {
  useAuditPruneApprovals,
  useDecideAuditPruneApproval,
  useResendAuditPruneDecision,
} from '../../hooks/useAdminOps';
import {
  PRUNE_APPROVAL_FILTERS,
  PruneApprovalStatusFilter,
  PruneDecision,
  ReferenceLabel,
  decisionLabel,
  isPrunePending,
  pruneApprovalStatusChip,
  pruneDecisionReference,
  pruneRequesterBlocker,
  pruneResendRequest,
  type PruneResend,
} from '../auditRetention.logic';
import {
  DetailField,
  DetailSection,
  FilterChip,
  LoadError,
  absolute,
  relative,
} from '../websiteLeads/parts';
import { BlockerHint, ReferenceField, monoValue } from './parts';

const EMPTY_HINTS: Record<AuditPruneApprovalStatusFilter, string> = {
  [PruneApprovalStatusFilter.ALL]: 'The retention workflow has not asked to prune any partition.',
  [PruneApprovalStatusFilter.PENDING]: 'No prune request is waiting for a decision.',
  [PruneApprovalStatusFilter.DECIDED]: 'No prune request has been decided or has expired yet.',
};

function PruneDetail({ approval }: { approval: AuditPruneApprovalItem }) {
  const decision = decisionLabel(approval.decision);
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <StatusChip {...pruneApprovalStatusChip(approval.status)} />
      </div>
      <DetailSection title="Request">
        <DetailField label="Partition" value={monoValue(approval.partition_name)} />
        <DetailField label="Policy version" value={monoValue(approval.policy_version)} />
        <DetailField label="Workflow run" value={monoValue(approval.run_id)} />
        <DetailField label="Requested by" value={monoValue(approval.requested_by)} />
        <DetailField label="Requested" value={absolute(approval.requested_at)} />
        <DetailField label="Expires" value={absolute(approval.expires_at)} />
      </DetailSection>
      <DetailSection title="Binding">
        <DetailField label="Verification id" value={monoValue(approval.verification_id)} />
        <DetailField label="Manifest hash" value={monoValue(approval.manifest_hash)} />
        <DetailField label="Authorization id" value={monoValue(approval.authorization_id)} />
        <DetailField label="Dry-run digest" value={monoValue(approval.dry_run_digest)} />
        <DetailField label="Binding digest" value={monoValue(approval.binding_digest)} />
      </DetailSection>
      <DetailSection title="Decision">
        <DetailField label="Decision" value={decision} />
        <DetailField label="Decided by" value={monoValue(approval.decided_by)} />
        <DetailField label={ReferenceLabel.DECISION} value={monoValue(approval.decision_ref)} />
        <DetailField label="Decided" value={absolute(approval.decided_at)} />
        <DetailField label="Pruned" value={absolute(approval.consumed_at)} />
      </DetailSection>
    </>
  );
}

function PruneDecisionForm({
  approval,
  onDone,
}: {
  approval: AuditPruneApprovalItem;
  onDone: () => void;
}) {
  const decide = useDecideAuditPruneApproval();
  const { user } = useAuth();
  const [decisionRef, setDecisionRef] = useState('');
  const separation = pruneRequesterBlocker(approval, user?.sub);
  const checked = pruneDecisionReference(approval, decisionRef);
  const blocker = separation ?? checked.blocker;
  const payload = separation === null ? checked.payload : null;
  const submit = (decision: AuditPruneDecision) =>
    payload === null
      ? undefined
      : () =>
          decide.mutate(
            { approvalId: approval.approval_id, decision, decisionRef: payload },
            { onSuccess: onDone }
          );
  const disabled = payload === null || decide.isPending;

  return (
    <div className="space-y-3">
      <ReferenceField
        id="audit-prune-decision-ref"
        label={ReferenceLabel.DECISION}
        value={decisionRef}
        onChange={setDecisionRef}
      />
      <p className="text-xs text-gray-text">
        Approving opens a dual-control request: a second admin must approve it in Approvals before
        the retention workflow is told to prune {approval.partition_name}. Rejecting takes effect
        immediately.
      </p>
      <BlockerHint blocker={blocker} />
      <div className="grid grid-cols-2 gap-2">
        <button
          className="btn-primary disabled:opacity-50"
          disabled={disabled}
          onClick={submit(PruneDecision.APPROVE)}
        >
          Approve prune
        </button>
        <button
          className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={disabled}
          onClick={submit(PruneDecision.REJECT)}
        >
          Reject prune
        </button>
      </div>
    </div>
  );
}

function PruneResendForm({
  approval,
  request,
}: {
  approval: AuditPruneApprovalItem;
  request: PruneResend;
}) {
  const resend = useResendAuditPruneDecision();
  const send = () =>
    resend.mutate({
      approvalId: approval.approval_id,
      decision: request.decision,
      decisionRef: request.decisionRef,
    });

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-text">
        If the retention workflow did not receive this decision, resend it. Nothing new is decided:
        the recorded {decisionLabel(approval.decision)?.toLowerCase()} decision and its reference{' '}
        {request.decisionRef} are sent to the workflow again.
      </p>
      {resend.isSuccess && (
        <p role="status" className="text-xs text-green-400">
          The retention workflow received the recorded decision.
        </p>
      )}
      {resend.isError && (
        <p role="alert" className="text-xs text-red-400">
          {resend.error.message}
        </p>
      )}
      <button
        className="btn-primary disabled:opacity-50"
        disabled={resend.isPending}
        onClick={send}
      >
        Resend decision to workflow
      </button>
    </div>
  );
}

function PruneDrawerFooter({
  approval,
  resend,
  canWrite,
  onDone,
}: {
  approval: AuditPruneApprovalItem;
  resend: PruneResend | null;
  canWrite: boolean;
  onDone: () => void;
}) {
  if (!canWrite) {
    return (
      <p className="text-xs text-gray-text">
        {resend === null
          ? 'Read only: deciding a prune request needs the admin role.'
          : 'Read only: resending a decision needs the admin role.'}
      </p>
    );
  }
  if (resend !== null) {
    return <PruneResendForm key={approval.approval_id} approval={approval} request={resend} />;
  }
  return <PruneDecisionForm key={approval.approval_id} approval={approval} onDone={onDone} />;
}

export default function PruneApprovalsTab({ canWrite }: { canWrite: boolean }) {
  const [status, setStatus] = useState<AuditPruneApprovalStatusFilter>(
    PruneApprovalStatusFilter.ALL
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queue = useAuditPruneApprovals({ status });
  const selected = queue.rows.find(row => row.approval_id === selectedId) ?? null;
  const closeDrawer = () => setSelectedId(null);
  const resend = selected === null ? null : pruneResendRequest(selected, Date.now());
  const actionable = selected !== null && (isPrunePending(selected) || resend !== null);

  const columns: QueueColumn<AuditPruneApprovalItem>[] = [
    {
      key: 'requested',
      header: 'Requested',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">{relative(row.requested_at)}</span>
      ),
    },
    {
      key: 'partition',
      header: 'Partition',
      render: row => <span className="font-mono text-xs break-all">{row.partition_name}</span>,
    },
    {
      key: 'policy',
      header: 'Policy',
      render: row => <span className="font-mono text-xs">{row.policy_version}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: row => <StatusChip {...pruneApprovalStatusChip(row.status)} />,
    },
    {
      key: 'expires',
      header: 'Expires',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {isPrunePending(row) ? relative(row.expires_at) : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div
        className="glass-card p-4 flex items-center gap-2 flex-wrap"
        role="group"
        aria-label="Prune status"
      >
        {PRUNE_APPROVAL_FILTERS.map(filter => (
          <FilterChip
            key={filter.value}
            active={status === filter.value}
            label={filter.label}
            onClick={() => {
              setStatus(filter.value);
              setSelectedId(null);
            }}
          />
        ))}
      </div>

      {queue.isError ? (
        <LoadError what="prune requests" error={queue.error} retry={() => queue.refetch()} />
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.approval_id}
          isLoading={queue.isPending}
          onRowClick={row => setSelectedId(row.approval_id)}
          rowActionLabel={row => `Open prune request ${row.partition_name}`}
          emptyTitle="No prune requests"
          emptyHint={EMPTY_HINTS[status]}
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title={selected ? `Prune ${selected.partition_name}` : 'Prune request'}
        onClose={closeDrawer}
        footer={
          actionable ? (
            <PruneDrawerFooter
              approval={selected}
              resend={resend}
              canWrite={canWrite}
              onDone={closeDrawer}
            />
          ) : null
        }
      >
        {selected && <PruneDetail approval={selected} />}
      </DetailDrawer>
    </div>
  );
}
